// src/rms/hooks/useReferralWizardState.js
/**
 * useReferralWizardState
 * ----------------------
 * What:
 *   A small, framework‑agnostic state container for the RMS wizard screens.
 *   It tracks the user's current answers, which section they're on, and exposes
 *   a few helpers for navigation and validation.
 *
 * Why:
 *   - Keep the visual layer (RmsFormTemplate) lean and focused on rendering.
 *   - Centralize common wizard mechanics (progress, visibility filtering).
 *   - Allow safe hydration (resume a draft) without re‑implementing logic in the UI.
 *
 * Key ideas:
 *   - answers is a flat map: { [questionId]: typedValue }. Groups/addresses are just
 *     structured values under a single key (e.g., address object).
 *   - sections comes from the server‑provided schema; we keep only a read‑only view.
 *   - visibleQuestions are computed from current answers using the same DSL as the server
 *     (see schemaEvaluator.js). This keeps client experience aligned with server authority.
 *
 * Autosave & hydration:
 *   - setAnswer updates only the changed question (used for per‑keystroke updates).
 *   - setAllAnswers replaces the full answers object (used to hydrate a saved draft).
 *     Important: callers should prefer setAllAnswers for one‑shot resume, otherwise
 *     call setAnswer for incremental changes. This helps avoid unnecessary re‑renders.
 *
 * Navigation:
 *   - next/back move a simple section index; RmsFormTemplate handles step‑level
 *     visibility and validation. We keep the hook generic and predictable.
 *
 * Validation:
 *   - validateConfirmFields illustrates how to implement cross‑field rules that
 *     may be reused by multiple templates (e.g., confirm email/phone "same as").
 *     Server remains the source of truth; this is UX guidance only.
 */
import { useCallback, useMemo, useState } from 'react';
import { isQuestionVisible, validateSameAs } from '../utils/schemaEvaluator';

/**
 * Create and manage wizard state derived from a server schema.
 * @param {{sections?: Array}} schema - The schema object or a stub ({ sections: [] }).
 * @returns {{
 *   answers: Record<string, unknown>,
 *   setAnswer: (id: string, value: unknown) => void,
 *   setAllAnswers: (all: Record<string, unknown>) => void,
 *   currentSectionIdx: number,
 *   setCurrentSectionIdx: (idx: number) => void,
 *   currentSection: object|undefined,
 *   visibleQuestions: Array,
 *   progress: { totalQuestions: number, answeredQuestions: number, percent: number },
 *   next: () => void,
 *   back: () => void,
 *   validateConfirmFields: () => boolean
 * }}
 */
export function useReferralWizardState(schema) {
  const [answers, setAnswers] = useState({});
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);

  // Derive sections once per schema change to avoid recomputing unnecessarily.
  const sections = useMemo(() => schema?.sections ?? [], [schema]);
  const currentSection = sections[currentSectionIdx];

  // Filter only those questions the user should see under current answers.
  const visibleQuestions = useMemo(() => {
    if (!currentSection) return [];
    return (currentSection.questions || []).filter(q => isQuestionVisible(answers, q.visibleWhen));
  }, [currentSection, answers]);

  // A simple progress proxy: total = declared questions, answered = non‑empty answers.
  // Note: this is a coarse UX signal; the server computes authoritative progress.
  const totalQuestions = useMemo(() => {
    return sections.reduce((acc, s) => acc + (s.questions?.length || 0), 0);
  }, [sections]);

  const answeredCount = useMemo(() => {
    return Object.values(answers).filter(v => v != null && String(v).trim() !== '').length;
  }, [answers]);

  const progress = useMemo(() => {
    const percent = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;
    return { totalQuestions, answeredQuestions: answeredCount, percent };
  }, [totalQuestions, answeredCount]);

  /**
   * Update a single answer by questionId. Use this for keystroke‑level updates.
   */
  const setAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);
  /**
   * Replace all answers in one shot. Use this to hydrate a saved draft.
   * Avoid calling from inside tight render loops to prevent re‑render churn.
   */
  const setAllAnswers = useCallback((all) => {
    setAnswers(all || {});
  }, []);

  /**
   * Move to next schema section. Bounds are guarded.
   */
  const next = useCallback(() => {
    setCurrentSectionIdx(i => Math.min(i + 1, sections.length - 1));
  }, [sections.length]);

  /**
   * Move back to previous schema section. Bounds are guarded.
   */
  const back = useCallback(() => {
    setCurrentSectionIdx(i => Math.max(i - 1, 0));
  }, []);

  /**
   * Example cross‑field validation helper (UX only).
   * Keeps client behavior consistent with server "sameAs" rules.
   */
  const validateConfirmFields = useCallback(() => {
    // Enforce sameAs for email/phone confirm fields if present
    const emailOk = validateSameAs(answers, 'ref_email_confirm', 'ref_email');
    const phoneOk = validateSameAs(answers, 'ref_phone_confirm', 'ref_phone');
    return emailOk && phoneOk;
  }, [answers]);

  return {
    answers,
    setAllAnswers,
    setAnswer,
    currentSectionIdx,
    setCurrentSectionIdx,
    currentSection,
    visibleQuestions,
    progress,
    next,
    back,
    validateConfirmFields
  };
}


